import { useQueryGetTarif } from '@workspace/core-ts/api';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { cn } from '@workspace/utils';
import { useEffect, useState } from 'react';
import { Button } from '../form/button';
import { LoaderScreen } from '../loader';
import { UnifiedTarifStep } from './component';
import type { TarifPageVariant } from './types';

interface UnifiedTarifContainerProps {
    variant: TarifPageVariant;
    className?: string;
    afterOptionsSlot?: React.ReactNode;
    children?: React.ReactNode;
    /** Transform the payload before sending to getTarif API */
    payloadTransform?: (state: Record<string, unknown>) => Record<string, unknown>;
}

export function UnifiedTarifContainer({ variant, className = 'max-w-4xl', afterOptionsSlot, children, payloadTransform }: UnifiedTarifContainerProps) {
    const { actions, meta, state } = useGlobalForm();
    const methods = useSmartFormGlobal();
    const [isReady, setIsReady] = useState(false);

    const payload = payloadTransform ? payloadTransform(state) : state;
    const queryTarif = useQueryGetTarif(meta.token, payload);

    useEffect(() => {
        if (!queryTarif.isSuccess && !queryTarif.isLoading && !queryTarif.isError) {
            queryTarif.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (queryTarif.isSuccess && queryTarif.data) {
            actions.saveData(queryTarif.data);

            // L'API retourne les données dans data.Tarif ou data.data.Tarif selon le format
            const Tarif = queryTarif.data.Tarif || queryTarif.data.data?.Tarif;

            if (Tarif) {
                methods.setValue('Tarif' as any, Tarif);

                // Présélection de la formule basée sur FormulePreconisee de l'API
                // (pour les avenants, l'API retourne la formule déjà souscrite)
                // On vérifie toujours que la formule sélectionnée existe dans la liste
                if (Tarif.ListeFormules?.length > 0) {
                    const currentFormule = methods.watch('DemandeTarif.Police.FormuleChoisie' as any);
                    const currentFormuleExists = currentFormule &&
                        Tarif.ListeFormules.some((f: any) => f.CodeFormule === currentFormule);

                    if (!currentFormuleExists) {
                        const formulePreconiseeExists = Tarif.FormulePreconisee &&
                            Tarif.ListeFormules.some((f: any) => f.CodeFormule === Tarif.FormulePreconisee);

                        const formuleToSet = formulePreconiseeExists
                            ? Tarif.FormulePreconisee
                            : Tarif.ListeFormules[0].CodeFormule;

                        methods.setValue('DemandeTarif.Police.FormuleChoisie' as any, formuleToSet);
                    }
                }

                // Pré-cochage des options basé sur Souscrite de l'API
                // (pour les avenants ou si DevoirConseil recommande l'option)
                const currentOptions = methods.watch('DemandeTarif.ListeOptions' as any) || [];
                if (currentOptions.length === 0 && Tarif.ListeFormules?.length > 0) {
                    const optionsSouscrites: Array<{ CodeOption: string; Souscrite: boolean }> = [];

                    // Parcourir les garanties de toutes les formules pour trouver les options souscrites
                    const Formule = Tarif.ListeFormules.find((f: any) => f.CodeFormule === Tarif.FormulePreconisee);
                    if (Formule?.ListeGaranties) {
                        Formule.ListeGaranties.forEach((garantie: any) => {
                            if (garantie.Disponible === 'Option' && garantie.Souscrite === true) {
                                // Éviter les doublons
                                if (!optionsSouscrites.some((opt) => opt.CodeOption === garantie.CodeGarantie)) {
                                    optionsSouscrites.push({
                                        CodeOption: garantie.CodeGarantie,
                                        Souscrite: true,
                                    });
                                }
                            }
                        });
                    }

                    if (optionsSouscrites.length > 0) {
                        methods.setValue('DemandeTarif.ListeOptions' as any, optionsSouscrites);
                    }
                }
                actions.saveData({...queryTarif.data, data: methods.getValues() });
            }

            setIsReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryTarif.isSuccess, queryTarif.data]);

    // Render only one SmartFormGlobal; switch content inside.
    let content: React.ReactNode;

    if (queryTarif.isLoading || queryTarif.isFetching) {
        content = (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <LoaderScreen />
                <p className="text-sm text-gray-500 mt-4">Calcul du tarif en cours...</p>
            </div>
        );
    } else if (queryTarif.isError) {
        const errorMessage = (queryTarif.error as any)?.response?.data?.error ?? 'Erreur lors de la récupération des tarifs';
        content = (
            <div className="mx-auto px-6 py-8 bg-red-50 text-center rounded-xl mt-10">
                <h2 className="text-xl font-medium text-red-900 mt-2">Impossible de calculer le tarif</h2>
                <p className="text-red-700 text-sm mt-2">{errorMessage}</p>
                <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
                    <Button
                        variant="outline"
                        onClick={() => actions.goPreviousQuestion()}
                        arrow={false}
                    >
                        Retour
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => queryTarif.refetch()}
                    >
                        Réessayer
                    </Button>
                </div>
            </div>
        );
    } else if (isReady) {
        content = (
            <>
                <UnifiedTarifStep variant={variant} afterOptionsSlot={afterOptionsSlot} beforeRecapSlot={children} />
            </>
        );
    } else {
        content = (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <LoaderScreen />
            </div>
        );
    }

    return (
        <SmartFormGlobal
            {...methods}
            className={cn(`mx-auto w-full`, className)}
        >
            {content}
        </SmartFormGlobal>
    );
}
